import Layout from '@components/Layout/Layout';
import { authOptions } from '@lib/auth/authOptions';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import { getServerSession } from 'next-auth';
import { getPromiseSettledValue } from 'utils';

import { AtomicCategoryFacetSection } from '@components/Search/AtomicCategoryFacetSection';
import { AtomicExternalWrapper } from '@components/Search/AtomicExternalWrapper';
import { AtomicPaginationSection } from '@components/Search/AtomicPaginationSection';
import { AtomicQueryRefinement } from '@components/Search/AtomicQueryRefinement';
import { AtomicResultsSection } from '@components/Search/AtomicResultsSection';
import { AtomicStatusSection } from '@components/Search/AtomicStatusSection';
import { SearchResultsBanner } from '@components/Search/SearchResultsBanner';
import { SearchTitle } from '@components/Search/SearchTitle';
import { MicrocopyProvider } from '@contexts/MicrocopyContext';
import { AtomicSearchContext } from '@contexts/coveo/AtomicSearchContext';
import {
  AtomicLayoutSection,
  AtomicNoResults,
  AtomicQueryError,
  AtomicSearchLayout,
} from '@coveo/atomic-react';
import { createContentfulCmsClient } from '@lib/cms/contentfulCMS';
import { AmplitudeExperimentsRemoteServer } from '@lib/flags';
import { useExposureEvent } from '@lib/flags/amplitude/client';
import { FLAG_TREATMENT, Flags } from '@lib/flags/flagConstants';
import { HttpStatusCode } from '@lib/http/HttpStatusCode';
import { mapContentfulSearchResultsBanners } from '@lib/util/banners';
import { MicrocopySlug } from '@lib/util/microcopy';
import { mapContentfulTags } from '@lib/util/tags';
import { useEffect } from 'react';

const SearchPage = ({
  session,
  layoutData,
  searchValuesMicrocopy,
  searchLabelsMicrocopy,
  productCardMicrocopy,
  paginationMicrocopy,
  productTags,
  searchResultsContent,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const mappedProductTags = productTags ? mapContentfulTags(productTags) : [];
  const mappedSearchResultsBanners = searchResultsContent?.bannersCollection
    ?.items
    ? mapContentfulSearchResultsBanners(
        searchResultsContent.bannersCollection.items
      )
    : [];

  const { exposureEvent, value } = useExposureEvent(Flags.COVEO_SEARCH);
  useEffect(() => {
    if (value) {
      /**
       * We're triggering the exposure event client-side instead of in getServerSideProps
       * to make sure that the user has seen the page before recording the event.
       */
      exposureEvent();
    }
  }, [value, exposureEvent]);

  return (
    <MicrocopyProvider
      value={{
        searchLabelsMicrocopy,
        searchValuesMicrocopy,
        productCardMicrocopy,
      }}
    >
      <Layout
        layoutData={layoutData}
        user={
          session?.user
            ? {
                name: session.user.name!,
                email: session.user.email!,
                profilePicture: session.user.image,
              }
            : undefined
        }
      >
        <div className="container min-h-[25rem] pb-10 pt-4 lg:pb-16 lg:pt-12">
          <AtomicExternalWrapper
            id="atomic-search-external"
            context={AtomicSearchContext}
            atomicSearchInterfaceSelector="#atomic-search-interface_search"
          >
            <style>
              {`
                atomic-search-layout {
                  grid-template-columns: repeat(12, minmax(0, 1fr)) !important;
                  column-gap: 1rem !important;
                }

                atomic-layout-section[section='facets'] {
                  grid-column: 1 / span 3 !important;
                }


                atomic-layout-section[section='main'] {
                  grid-column: 1 / span 12 !important;
                }

                @media (min-width: 1024px) {
                  atomic-layout-section[section='main'] {
                    grid-column: 4 / span 9 !important;
                  }

                  /* the following rules are usually set by atomic but it relies on being inside an atomic-search-interface, not an atomic-external */
                  atomic-external#atomic-search-external atomic-search-layout {
                    grid-template-areas: '. . atomic-section-search .'
                    '. atomic-section-facets atomic-section-main   .'
                    '. atomic-section-facets . .';
                  }
                  
                  atomic-search-layout atomic-layout-section[section="facets"] {
                    display: block;
                  }
                }

                atomic-no-results::part(icon), atomic-query-error::part(icon) {
                  display: none;
                }

                atomic-query-error::part(title) {
                  margin-top: unset;
                }

                @media (min-width: 768px) {
                  atomic-query-error::part(title) {
                    margin-top: -3rem;
                  }
                  atomic-no-results::part(no-results) {
                    margin-top: -2rem;
                  }
                }

                @media (min-width: 1024px) {
                  atomic-query-error::part(title) {
                    margin-top: -2rem;
                  }
                  atomic-no-results::part(no-results) {
                    margin-top: unset;
                  }
                }
              `}
            </style>
            <SearchResultsBanner
              context={AtomicSearchContext}
              banners={mappedSearchResultsBanners}
            />
            <SearchTitle context={AtomicSearchContext} />
            <AtomicSearchLayout>
              <AtomicLayoutSection section="facets">
                <AtomicCategoryFacetSection />
              </AtomicLayoutSection>
              <AtomicLayoutSection section="main">
                <div className="mb-8 lg:mb-6">
                  <AtomicQueryRefinement context={AtomicSearchContext} />
                </div>
                <AtomicStatusSection />
                <AtomicResultsSection
                  productTags={mappedProductTags}
                  context={AtomicSearchContext}
                />
                <AtomicPaginationSection
                  paginationMicrocopySet={paginationMicrocopy}
                  context={AtomicSearchContext}
                />
              </AtomicLayoutSection>
            </AtomicSearchLayout>
            <AtomicQueryError />
            <AtomicNoResults />
          </AtomicExternalWrapper>
        </div>
      </Layout>
    </MicrocopyProvider>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const locale = context.query.locale as string;
  const isDraftMode = context.draftMode ?? false;

  const contentfulCMSClient = createContentfulCmsClient(locale, isDraftMode);
  const session = await getServerSession(
    context.req,
    context.res,
    authOptions()
  );
  const userId = session?.accessToken?.genesis_user;
  const experimentsClient = new AmplitudeExperimentsRemoteServer({
    userId: typeof userId === 'string' ? userId : undefined,
    cookies: context.req.cookies,
  });

  const [
    layout,
    allFlagVariants,
    searchValuesMicrocopy,
    searchLabelsMicrocopy,
    productCardMicrocopy,
    paginationMicrocopy,
    productTags,
    searchResultsContent,
  ] = await Promise.allSettled([
    contentfulCMSClient.getLayout(),
    experimentsClient.getAllVariants(),
    contentfulCMSClient.getMicrocopySet(MicrocopySlug.SEARCH_VALUES),
    contentfulCMSClient.getMicrocopySet(MicrocopySlug.SEARCH),
    contentfulCMSClient.getMicrocopySet(MicrocopySlug.PRODUCT_CARD),
    contentfulCMSClient.getMicrocopySet(MicrocopySlug.PAGINATION),
    contentfulCMSClient.getTags(),
    contentfulCMSClient.getSearchResultsContent(),
  ]);

  const flagVariants = getPromiseSettledValue(allFlagVariants);
  if (flagVariants?.[Flags.COVEO_SEARCH]?.value !== FLAG_TREATMENT) {
    // if user isn't targeted for the coveo flag, redirect them to the default search page
    // To improve: we should try and parse the query params and redirect them to the correct search results page
    return {
      redirect: {
        destination: '/?orderBy=1',
        statusCode: HttpStatusCode.TemporaryRedirect,
      },
    };
  }

  return {
    props: {
      session,
      layoutData: getPromiseSettledValue(layout),
      searchValuesMicrocopy: getPromiseSettledValue(searchValuesMicrocopy),
      searchLabelsMicrocopy: getPromiseSettledValue(searchLabelsMicrocopy),
      productCardMicrocopy: getPromiseSettledValue(productCardMicrocopy),
      paginationMicrocopy: getPromiseSettledValue(paginationMicrocopy),
      productTags: getPromiseSettledValue(productTags),
      searchResultsContent: getPromiseSettledValue(searchResultsContent),

      // Passing props below so they are available in _app.tsx
      flagVariants: flagVariants ?? null,
      draft: isDraftMode,
    },
  };
};

export default SearchPage;
